// Converts a legacy fixed-layout campaign (subject + body + CTA + logo +
// coupon) into blocks when it is opened in the block editor. Layout campaigns
// that are never opened keep sending through the old layout renderer.

import { escapeHtml } from './escape'
import { makeBlock } from './makeBlock'
import type { Block } from './types'

const imgBlock = (el: Element): Block =>
  makeBlock('image', {
    src: el.getAttribute('src') ?? '',
    alt: el.getAttribute('alt') ?? '',
  })

// An element holding only <img> children (and whitespace), e.g. <p><img></p>.
const onlyImages = (el: Element): Element[] | null => {
  const imgs: Element[] = []
  for (const n of Array.from(el.childNodes)) {
    if (n.nodeType === 3 && !(n.textContent ?? '').trim()) continue
    if (n.nodeType === 1 && (n as Element).tagName.toLowerCase() === 'img') {
      imgs.push(n as Element)
      continue
    }
    if (n.nodeType === 1 && (n as Element).tagName.toLowerCase() === 'br')
      continue
    return null
  }
  return imgs.length ? imgs : null
}

// Splits the legacy rich-text body so nothing is silently dropped on re-save:
// top-level images become image blocks and h1–h3 become heading text blocks
// (h1 preset for h1, h2 preset for h2/h3), in order; everything else is
// grouped into paragraph text blocks (sanitised by the compiler).
function bodyToBlocks(html: string): Block[] {
  const para = (h: string) =>
    makeBlock('text', { preset: 'p', size: 15, html: h })
  if (!html.trim() || typeof DOMParser === 'undefined') return [para(html)]
  const doc = new DOMParser().parseFromString(
    `<!doctype html><body>${html}</body>`,
    'text/html',
  )
  const out: Block[] = []
  let buf = ''
  const flush = () => {
    if (buf.trim()) out.push(para(buf))
    buf = ''
  }
  for (const n of Array.from(doc.body.childNodes)) {
    if (n.nodeType !== 1) {
      if (n.nodeType === 3) buf += escapeHtml(n.textContent ?? '')
      continue
    }
    const el = n as Element
    const tag = el.tagName.toLowerCase()
    if (tag === 'img') {
      flush()
      out.push(imgBlock(el))
    } else if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
      flush()
      const preset = tag === 'h1' ? 'h1' : 'h2'
      out.push(
        makeBlock('text', {
          preset,
          size: preset === 'h1' ? 30 : 22,
          html: el.innerHTML,
        }),
      )
    } else {
      const imgs = onlyImages(el)
      if (imgs) {
        flush()
        for (const img of imgs) out.push(imgBlock(img))
      } else buf += el.outerHTML
    }
  }
  flush()
  return out.length ? out : [para('')]
}

export function layoutToBlocks(
  c: {
    subject: string
    body_html: string
    cta_text: string
    cta_url: string
    logo_url: string | null
    coupon_code: string
  },
  brandLogoUrl: string,
): Block[] {
  const logo = String(c.logo_url ?? '').trim()
  const out: Block[] = [
    // An explicit logo different from the store's brand logo is kept; otherwise
    // the header follows the brand logo at compile time.
    makeBlock('header', {
      logoImg: logo && logo !== brandLogoUrl ? logo : '',
      showLinks: false,
    }),
    makeBlock('text', {
      preset: 'h1',
      size: 30,
      html: escapeHtml(c.subject ?? ''),
    }),
    ...bodyToBlocks(c.body_html ?? ''),
  ]
  const code = String(c.coupon_code ?? '').trim()
  if (code) out.push(makeBlock('coupon', { code }))
  const url = String(c.cta_url ?? '').trim()
  if (url)
    out.push(
      makeBlock('button', { label: c.cta_text?.trim() || 'Shop now', url }),
    )
  out.push(makeBlock('footer'))
  return out
}
